// frontend/src/pages/SourcesPage.jsx
import React, { useState, useEffect } from 'react';
import { getTopics } from '../services/api';
import api from '../services/api';
import { BookOpen, ExternalLink, Loader, Filter } from 'lucide-react';

const SourcesPage = () => {
  const [sources, setSources] = useState([]);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTopic, setSelectedTopic] = useState('');

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      const [topicsRes] = await Promise.all([
        getTopics()
      ]);
      setTopics(topicsRes.data);

      // Load sources for all topics
      await loadSources();
    } catch (err) {
      console.error('Failed to load data', err);
    } finally {
      setLoading(false);
    }
  };

  const loadSources = async (topicId = '') => {
    try {
      const params = topicId ? { topic_id: topicId } : {};
      const res = await api.get('/api/sources', { params });
      setSources(res.data);
    } catch (err) {
      console.error('Failed to load sources', err);
      setSources([]);
    }
  };

  const handleTopicFilter = (topicId) => {
    setSelectedTopic(topicId);
    loadSources(topicId);
  };

  const getTopicName = (topicId) => {
    const topic = topics.find((t) => t.id === topicId);
    return topic ? topic.name : 'Unknown';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="animate-spin text-brand-mid" size={32} />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-brand-dark">Source Library</h1>
        <p className="text-gray-500 mt-1">Articles retrieved from research runs</p>
      </div>

      {/* Filter */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-gray-500">
            <Filter size={18} />
            <span className="text-sm font-medium">Filter by topic:</span>
          </div>
          <select
            value={selectedTopic}
            onChange={(e) => handleTopicFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-mid text-sm"
          >
            <option value="">All Topics</option>
            {topics.map((topic) => (
              <option key={topic.id} value={topic.id}>{topic.name}</option>
            ))}
          </select>
          <span className="text-sm text-gray-400">
            {sources.length} source{sources.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Sources List */}
      {sources.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <BookOpen size={40} className="mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500">No sources yet. Run a research pipeline to retrieve articles.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {sources.map((source) => (
            <div
              key={source.id}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <span className="text-xs px-2 py-1 rounded-full bg-brand-light text-brand-dark">
                      {getTopicName(source.topic_id)}
                    </span>
                    {source.author && (
                      <span className="text-xs text-gray-400">by {source.author}</span>
                    )}
                    <span className="text-xs text-gray-400">
                      {new Date(source.retrieved_at).toLocaleDateString()}
                    </span>
                  </div>
                  <h3 className="text-sm font-medium text-gray-800 mb-1">
                    {source.title || 'Untitled'}
                  </h3>
                  {source.content && (
                    <p className="text-xs text-gray-500 line-clamp-2 mb-1">
                      {source.content.substring(0, 250)}...
                    </p>
                  )}
                  <p className="text-xs text-gray-400 truncate">{source.url}</p>
                </div>
                
                  <a href={source.url} target="_blank" rel="noopener noreferrer"
                  className="p-2 text-gray-400 hover:text-brand-mid transition-colors ml-3"
                  title="Open original article">
                  <ExternalLink size={16} />
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SourcesPage;